import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import { NavigationRange } from '@/pages/Onboarding/components/NavigationRange';
import { OptionItemSquareList } from '@/pages/Onboarding/components/OptionItemSquare/OptionItemSquare';
import { OptionItemSquareLight } from '@/pages/Onboarding/components/OptionItemSquareLight';
import { useOnboarding } from '@/pages/Onboarding/context';
import { BODY_SHAPE_OPTIONS } from '@/pages/Onboarding/options/BodyShape/BODY_SHAPE_OPTIONS';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { OwnAiTitle, OwnAiTitleHighlight } from '../../components/Title';
import { LayoutDataType } from '../../types';

import { ScreenBodyShapeStyles } from './BodyShape.styles';

export const ScreenBodyShape: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'BodyShape',
      });
    }
  }, []);

  const shouldAnimate = webCampaign.isAnimated();
  const [showElements, setShowElements] = React.useState<boolean>(true);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const body_shape = form.watch('body_shape');

  const handleSelect = (
    option: OnbOptionType<LayoutDataType['body_shape']>,
  ) => {
    form.setValue('body_shape', option.id);
    userService.createOrUpdate({
      onboarding_data: {
        body_shape: option.title,
      },
    });

    if (shouldAnimate) {
      setTimeout(() => {
        if (shouldAnimate) {
          setShowElements(false);
        }
        nextStepTimeout(wizard.nextStep);
      }, 200);
    } else {
      nextStepTimeout(wizard.nextStep);
    }
  };

  const handleBackStep = (): void => {
    if (shouldAnimate) {
      setShowElements(false);
      nextStepTimeout(() => wizard.goToStep('age'));
    } else {
      wizard.goToStep('age');
    }
  };

  return (
    <ScreenBodyShapeStyles background="dark-violet">
      <NavigationRange onBack={handleBackStep} />

      <OwnAiTitle
        animate={shouldAnimate}
        show={showElements}
        key="body-shape-title"
      >
        Choose her <OwnAiTitleHighlight>body shape</OwnAiTitleHighlight>
      </OwnAiTitle>

      <IonContent>
        <OptionItemSquareList>
          {BODY_SHAPE_OPTIONS.map((option) => (
            <OptionItemSquareLight
              key={option.id}
              isSelected={body_shape === option.id}
              onClick={() => handleSelect(option)}
              bgSrc={`https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/layout-own-ai/body_shape/${option.id}.png`}
              title={option.title}
              TEST_ID={`body_shape-${option.id}`}
              animate={shouldAnimate}
              show={showElements}
            />
          ))}
        </OptionItemSquareList>
      </IonContent>
    </ScreenBodyShapeStyles>
  );
};
